import React, { useState, useEffect, useRef } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { getPerformance, getPerformanceByCampaign } from '../api';

interface DayData { date: string; impressions: number; leads: number; label: string; }

const formatK = (v: number) => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v);

const Tip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e8e8ed', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {payload.map((p: any) => (
        <div key={p.name} style={{ color: p.color }}>{p.name}: <strong>{p.name === 'Leads' ? p.value : formatK(p.value)}</strong></div>
      ))}
    </div>
  );
};

interface Props {
  filteredAccountIds?: string[];
  campaignPattern?: string;
}

export default function PerformanceGraph({ filteredAccountIds, campaignPattern }: Props) {
  const [data, setData] = useState<DayData[]>([]);
  const [loading, setLoading] = useState(true);
  const [cached, setCached] = useState(false);
  const [campCount, setCampCount] = useState(0);
  const prevKey = useRef<string>('');

  const effectKey = [
    filteredAccountIds ? [...filteredAccountIds].sort().join(',') : '',
    campaignPattern ?? '',
  ].join('|');

  useEffect(() => {
    if (effectKey === prevKey.current && data.length > 0) return;
    prevKey.current = effectKey;
    setLoading(true);

    const isCampaignMode = !!(campaignPattern && filteredAccountIds?.length);
    const fetch = isCampaignMode
      ? getPerformanceByCampaign(campaignPattern!, filteredAccountIds!)
      : getPerformance(filteredAccountIds?.length ? filteredAccountIds : undefined);

    fetch
      .then(res => {
        const chart = res.data.chart.map((d: any) => ({
          ...d, label: d.date.slice(5).split('-').reverse().join('.'),
        }));
        setData(chart);
        setCached(res.data.cached || false);
        setCampCount(res.data.campaign_count || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [effectKey]);

  const badge = campaignPattern && campCount > 0
    ? `${campCount} Kampagnen`
    : filteredAccountIds?.length
    ? `${filteredAccountIds.length} gefiltert`
    : cached ? 'gecacht' : 'live';

  const loadingLabel = campaignPattern
    ? `Lade Kampagnen-Insights für "${campaignPattern}"…`
    : filteredAccountIds?.length
    ? 'Lade gefilterte Accounts…'
    : 'Lade alle Accounts…';

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <span style={s.title}>
          {campaignPattern ? `Kampagne: "${campaignPattern}"` : 'Performance'} · 30 Tage
        </span>
        <span style={s.badge}>{badge}</span>
      </div>
      {loading ? (
        <div style={s.loading}>
          <div style={s.spinner} />
          <span style={{ color: '#aeaeb2', fontSize: 12 }}>{loadingLabel}</span>
        </div>
      ) : data.length === 0 ? (
        <div style={s.loading}>
          <span style={{ color: '#aeaeb2', fontSize: 12 }}>Keine Daten verfügbar</span>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={data} margin={{ top: 4, right: 40, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f5" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#aeaeb2' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
            <YAxis yAxisId="l" tick={{ fontSize: 10, fill: '#1a3a5c' }} tickLine={false} axisLine={false} width={28} />
            <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: '#aeaeb2' }} tickLine={false} axisLine={false} tickFormatter={formatK} width={38} />
            <Tooltip content={<Tip />} />
            <Line yAxisId="l" type="monotone" dataKey="leads" name="Leads" stroke="#1a3a5c" strokeWidth={2} dot={false} activeDot={{ r: 3 }} />
            <Line yAxisId="r" type="monotone" dataKey="impressions" name="Impressionen" stroke="#c7d2dd" strokeWidth={1.5} dot={false} activeDot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  wrap: {
    background: '#fff', border: '1px solid #e8e8ed', borderRadius: 14,
    padding: '16px 18px', flex: 1, minWidth: 0,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  title: { fontSize: 13, fontWeight: 600, color: '#1d1d1f' },
  badge: { fontSize: 11, color: '#aeaeb2', background: '#f5f5f7', borderRadius: 6, padding: '2px 7px' },
  loading: { display: 'flex', alignItems: 'center', gap: 10, height: 160, justifyContent: 'center' },
  spinner: {
    width: 18, height: 18, border: '2px solid #e8e8ed',
    borderTop: '2px solid #1a3a5c', borderRadius: '50%', animation: 'spin 0.7s linear infinite',
  },
};
